import type { ComponentType, ReactNode } from 'react';

/** Heroicons / lucide-shaped icon component. */
export type NavIcon = ComponentType<{ className?: string }>;

/**
 * One entry in a sidebar or user-menu tree.
 *
 * A single flexible shape (rather than a discriminated union) keeps the app-side
 * config arrays terse — the renderer decides what an entry is from which fields
 * are present:
 *   `children` → collapsible group   `section` → uppercase label
 *   `render`   → app-supplied slot   otherwise → plain link (needs `to`)
 */
export interface NavNode {
  /** Display label. Also the identity used for persisted group expand state. */
  name: string;
  /** Route path. Required for plain links; ignored for groups/sections/slots. */
  to?: string;
  icon?: NavIcon;
  /** RBAC key. A group is hidden when every child is denied. */
  permission?: string;
  /** Visible if ANY of these pass. Use for pages that merge two RBAC surfaces. */
  anyPermission?: string[];
  /** Visible only if ALL of these pass. Use for pages that combine two scopes. */
  allPermissions?: string[];
  /** Children make this a collapsible group. */
  children?: NavNode[];
  /** Match `to` exactly instead of as a path prefix (use for the "/" root). */
  end?: boolean;
  /** Render as a non-clickable section label (e.g. GLOBAL / LOCAL). */
  section?: boolean;
  /** Arbitrary content slotted inline (e.g. the cloud node selector). */
  render?: (ctx: { collapsed: boolean }) => ReactNode;
  /**
   * Extra path prefixes that also light this item up — matched as plain
   * `startsWith`, so `/cameras/` catches the detail routes without catching
   * `/cameras` itself (which belongs to a different menu entry).
   */
  alsoActiveFor?: string[];
  /** Hide without removing from config — handy while a page is still landing. */
  hidden?: boolean;
  /**
   * Model-gated entry: shown only when this node has an ACTIVE analytics model for the
   * named surface (a key of MODEL_SURFACE_RULES — the report slugs). Used by the per-model
   * report entries, which are dead ends when the model isn't attached to any camera.
   *
   * Ignored unless the renderer is given a `hasModel` predicate, so a tree rendered without
   * one (the user menu) keeps every entry.
   */
  requiresModel?: string;
}

export type HasPermission = (permission: string) => boolean;

/** Whether this node has an active model for a gated surface (see NavNode.requiresModel). */
export type HasModel = (surface: string) => boolean;

/**
 * Drop denied, hidden and model-gated entries. Groups survive only if at least one child
 * does, so a group header never renders above an empty list.
 */
export function filterNav(items: NavNode[], hasPermission: HasPermission, hasModel?: HasModel): NavNode[] {
  const out: NavNode[] = [];
  for (const item of items) {
    if (item.hidden) continue;
    if (item.permission && !hasPermission(item.permission)) continue;
    if (item.anyPermission && !item.anyPermission.some(hasPermission)) continue;
    if (item.allPermissions && !item.allPermissions.every(hasPermission)) continue;
    if (item.requiresModel && hasModel && !hasModel(item.requiresModel)) continue;
    if (item.children) {
      const children = filterNav(item.children, hasPermission, hasModel);
      if (children.length === 0) continue;
      out.push({ ...item, children });
      continue;
    }
    out.push(item);
  }
  // A section label with nothing after it is noise.
  return out.filter((item, i) => !item.section || out.slice(i + 1).some((n) => !n.section));
}

/** True when `pathname` is inside `node`'s route (exact for `end` nodes). */
export function isNodeActive(node: NavNode, pathname: string): boolean {
  if (node.to) {
    const hit = node.end
      ? pathname === node.to
      : pathname === node.to || pathname.startsWith(node.to + '/');
    if (hit) return true;
  }
  return (node.alsoActiveFor || []).some((prefix) => pathname.startsWith(prefix));
}

/** True when any descendant of `node` matches `pathname`. */
export function isBranchActive(node: NavNode, pathname: string): boolean {
  if (node.children) return node.children.some((c) => isBranchActive(c, pathname));
  return isNodeActive(node, pathname);
}
